import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, type ApiSuccessResponse } from "../api-client";
import { useCompanyId } from "./use-company";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SearchResultItem {
  id: string;
  module: string;
  doctype: string;
  title: string;
  subtitle?: string;
  link: string;
}

export interface SearchResultGroup {
  group: string;
  module: string;
  doctype: string;
  icon: string;
  items: SearchResultItem[];
}

export interface SearchResponse {
  results: SearchResultGroup[];
  total: number;
}

export interface RecentSearchItem {
  id: string;
  query: string;
  resultModule?: string;
  resultDoctype?: string;
  resultDocumentId?: string;
  resultTitle?: string;
  searchedAt: string;
}

// ---------------------------------------------------------------------------
// Debounce hook (inline — no external dep)
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// ---------------------------------------------------------------------------
// useGlobalSearch
// ---------------------------------------------------------------------------

export function useGlobalSearch() {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  // State
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  // ---- Search API ----
  const searchQuery = useQuery<ApiSuccessResponse<SearchResponse>>({
    queryKey: ["global-search", debouncedQuery, companyId],
    queryFn: () =>
      apiClient.get<ApiSuccessResponse<SearchResponse>>("/core/search", {
        q: debouncedQuery,
        companyId,
      }),
    enabled: !!debouncedQuery && debouncedQuery.length >= 2 && !!companyId,
    staleTime: 30_000, // 30s
  });

  // ---- Recent searches ----
  const recentQuery = useQuery<ApiSuccessResponse<RecentSearchItem[]>>({
    queryKey: ["search-recent", companyId],
    queryFn: () =>
      apiClient.get<ApiSuccessResponse<RecentSearchItem[]>>(
        "/core/search/recent",
        { companyId },
      ),
    enabled: !!companyId,
    staleTime: 60_000, // 1 min
  });

  // ---- Log search click ----
  const logMutation = useMutation({
    mutationFn: (item: SearchResultItem) =>
      apiClient.post("/core/search/log", {
        query: debouncedQuery || query,
        resultModule: item.module,
        resultDoctype: item.doctype,
        resultDocumentId: item.id,
        resultTitle: item.title,
        companyId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-recent", companyId] });
    },
  });

  // ---- Clear history ----
  const clearMutation = useMutation({
    mutationFn: () => apiClient.delete("/core/search/recent", { companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-recent", companyId] });
    },
  });

  // ---- Reset ----
  const reset = useCallback(() => {
    setQuery("");
  }, []);

  return {
    query,
    setQuery,
    debouncedQuery,
    results: searchQuery.data?.data ?? null,
    recent: recentQuery.data?.data ?? [],
    isLoading: searchQuery.isFetching,
    isRecentLoading: recentQuery.isLoading,
    logSearch: logMutation.mutate,
    clearHistory: clearMutation.mutate,
    reset,
  };
}
